import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['button', 'frame', 'icon', 'status', 'viewport'];
  static values = {
    iframeSrc: String,
    iframeWidth: { type: Number, default: 1920 },
    iframeHeight: { type: Number, default: 1080 },
    storageKey: String
  };

  connect() {
    this.resizeObserver = new ResizeObserver(() => this.#queueScale());
    this.resizeObserver.observe(this.viewportTarget);
    this.#syncNativeSize();
    this.#sync();
  }

  disconnect() {
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.scaleFrame);
  }

  expand() {
    this.expanded = true;
    this.#sync();
  }

  toggle() {
    this.dispatch('before-toggle');
    this.expanded = !this.expanded;
    this.#sync();
  }

  refresh() {
    this.#syncNativeSize();
    this.#sync();
  }

  morphed(event) {
    if (event.target === this.element) this.refresh();
  }

  iframeWidthValueChanged() {
    this.#syncNativeSize();
  }

  iframeHeightValueChanged() {
    this.#syncNativeSize();
  }

  get expanded() {
    const stored = this.expandedState ?? this.#storedValue();
    return stored === null ? true : stored === 'true';
  }

  set expanded(value) {
    this.expandedState = String(value);
    this.#setStoredValue(value);
  }

  #sync() {
    this.element.classList.toggle('is-minimized', !this.expanded);

    if (this.expanded) {
      this.#loadIframe();
      this.#queueScale();
    } else {
      this.#unloadIframe();
    }

    this.#syncControls();
    this.dispatch('change', { detail: { minimized: !this.expanded } });
    // The floating window has applied its dimensions; size the iframe before its first paint.
    this.#updateScale();
  }

  #loadIframe() {
    if (!this.hasFrameTarget) {
      this.#setStatus('Offline');
      return;
    }

    const existingIframe = this.frameTarget.querySelector('iframe');
    if (existingIframe) {
      this.#setStatus(existingIframe.dataset.loaded === 'true' ? 'Live' : 'Loading');
      existingIframe.addEventListener('load', () => this.#markIframeLoaded(existingIframe), { once: true });
      return;
    }

    const iframe = document.createElement('iframe');
    iframe.className = 'dm-screen-preview__iframe';
    iframe.title = 'Live game screen preview';
    iframe.loading = 'lazy';
    iframe.src = this.iframeSrcValue;
    iframe.addEventListener('load', () => this.#markIframeLoaded(iframe), { once: true });

    this.#setStatus('Loading');
    this.frameTarget.replaceChildren(iframe);
  }

  #unloadIframe() {
    if (this.hasFrameTarget) this.frameTarget.replaceChildren();
    this.#setStatus(this.hasFrameTarget ? 'Live' : 'Offline');
  }

  #syncControls() {
    const label = this.expanded ? 'Minimize preview' : 'Restore preview';

    this.buttonTarget.setAttribute('aria-expanded', String(this.expanded));
    this.buttonTarget.setAttribute('aria-label', label);
    this.buttonTarget.setAttribute('title', label);
    this.iconTarget.classList.toggle('fa-window-minimize', this.expanded);
    this.iconTarget.classList.toggle('fa-up-right-and-down-left-from-center', !this.expanded);
  }

  #syncNativeSize() {
    this.element.style.setProperty('--game-screen-preview-width', this.#nativeWidth());
    this.element.style.setProperty('--game-screen-preview-height', this.#nativeHeight());
    this.#queueScale();
  }

  #queueScale() {
    cancelAnimationFrame(this.scaleFrame);
    this.scaleFrame = requestAnimationFrame(() => this.#updateScale());
  }

  #updateScale() {
    if (!this.expanded) return;

    // Layout dimensions exclude the floating window's animated transform.
    const width = this.viewportTarget.clientWidth;
    const height = this.viewportTarget.clientHeight;
    if (width === 0 || height === 0) return;

    const scale = Math.min(width / this.#nativeWidth(), height / this.#nativeHeight());
    this.element.style.setProperty('--game-screen-preview-scale', scale);
  }

  #setStatus(status) {
    this.statusTarget.textContent = status;
    this.element.classList.toggle('is-loading', status === 'Loading');
  }

  #markIframeLoaded(iframe) {
    if (!iframe.isConnected) return;
    iframe.dataset.loaded = 'true';
    this.#setStatus('Live');
  }

  #storedValue() {
    try {
      return window.localStorage.getItem(this.storageKeyValue);
    } catch {
      return null;
    }
  }

  #setStoredValue(value) {
    try {
      window.localStorage.setItem(this.storageKeyValue, value ? 'true' : 'false');
    } catch {
      // Ignore storage failures; the preview still works for the current page.
    }
  }

  #nativeWidth() {
    return this.#positiveNumberOrDefault(this.iframeWidthValue, 1920);
  }

  #nativeHeight() {
    return this.#positiveNumberOrDefault(this.iframeHeightValue, 1080);
  }

  #positiveNumberOrDefault(value, fallback) {
    return Number.isFinite(value) && value > 0 ? value : fallback;
  }
}
